import { Link } from 'react-router-dom';
import { Minus, Plus, ShoppingBag, X } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useShopCart, useUpdateShopCartItem } from '@/hooks/useShop';
import { formatPrice } from '@/lib/pricing';

const FREE_SHIPPING_THRESHOLD = 9000;

interface ShopMiniCartProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function ShopMiniCart({ open, onOpenChange }: ShopMiniCartProps) {
  const { data: cart } = useShopCart();
  const updateItem = useUpdateShopCartItem();

  const subtotal = cart?.subtotal_cents || 0;
  const remaining = Math.max(FREE_SHIPPING_THRESHOLD - subtotal, 0);
  const progress = Math.min((subtotal / FREE_SHIPPING_THRESHOLD) * 100, 100);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex flex-col w-full max-w-md bg-card border-border">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <ShoppingBag className="h-5 w-5 text-primary" />
            Panier ({cart?.items?.length || 0})
          </SheetTitle>
        </SheetHeader>

        {/* Free shipping bar */}
        <div className="px-1 space-y-2">
          {remaining > 0 ? (
            <p className="text-sm text-muted-foreground">
              Plus que <span className="font-semibold text-primary">{formatPrice(remaining)}</span> pour la livraison offerte
            </p>
          ) : (
            <p className="text-sm font-semibold text-primary">🎉 Livraison offerte !</p>
          )}
          <Progress value={progress} className="h-2" />
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto space-y-3 py-4">
          {cart?.items?.length ? cart.items.map((item) => (
            <div key={item.id} className="flex gap-3 rounded-lg border border-border p-3">
              <div className="h-16 w-16 rounded-lg bg-secondary shrink-0 overflow-hidden">
                {item.image_url ? (
                  <img src={item.image_url} alt={item.product?.name || ''} className="h-full w-full object-cover" loading="lazy" />
                ) : (
                  <div className="h-full w-full grid place-items-center text-muted-foreground">
                    <ShoppingBag className="h-5 w-5" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{item.product?.name}</p>
                <p className="text-xs text-muted-foreground">{item.variant?.name}</p>
                <p className="text-sm font-semibold text-primary mt-1">{formatPrice(item.line_total_cents)}</p>
              </div>
              <div className="flex flex-col items-center gap-1">
                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => updateItem.mutate({ itemId: item.id, quantity: item.quantity + 1 })}>
                  <Plus className="h-3 w-3" />
                </Button>
                <span className="text-xs font-medium">{item.quantity}</span>
                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => updateItem.mutate({ itemId: item.id, quantity: item.quantity - 1 })}>
                  {item.quantity === 1 ? <X className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
                </Button>
              </div>
            </div>
          )) : (
            <div className="py-12 text-center text-muted-foreground text-sm">Ton panier est vide</div>
          )}
        </div>

        {/* Footer */}
        {(cart?.items?.length ?? 0) > 0 && (
          <div className="space-y-3 border-t border-border pt-4">
            <div className="flex items-center justify-between font-semibold">
              <span>Sous-total</span>
              <span>{formatPrice(subtotal)}</span>
            </div>
            <Button asChild className="w-full" size="lg" onClick={() => onOpenChange(false)}>
              <Link to="/boutique/panier">Voir le panier</Link>
            </Button>
            <Button variant="outline" className="w-full" onClick={() => onOpenChange(false)}>
              Continuer les achats
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}